import axios, { AxiosTransformer } from 'axios';
import { get, post } from 'api/client';
import PageTypeUtils from 'app/utils/pageType';
import Config from 'config';
import { SAFE_CONNECT_CONFIRM_TIMEOUT } from 'constants/request';
import {
  SocialLoginBusiness,
  SocialLoginConfirmPortfolio,
  SocialLoginEmptyReason,
  SocialLoginPage,
  SocialLoginRefusedPage,
  SocialLoginTokenType,
} from 'types/SocialLogin';
import { TYPE } from '@kontentino/kontentino-constants/Pages';

/**
 * social-login answers the confirm with `{ status, data, refused }`. The shared
 * `responseChecker` unwraps any body that has `data` down to `data` alone,
 * which would drop `refused`; it keeps the whole envelope only when the body
 * also has `meta` (or `pagination`). So, after the default JSON parse, give a
 * body whose `data` is the page list a `meta` (the same trick billing's
 * invoices use). An error body (`{ status: false, outcome, data: {message} }`)
 * has no page list and passes through untouched.
 */
const keepPagesEnvelope: AxiosTransformer = (body) =>
  body && typeof body === 'object' && Array.isArray(body.data) && !body.meta
    ? { ...body, meta: {} }
    : body;

const pagesConfirmTransforms = ([] as AxiosTransformer[]).concat(
  axios.defaults.transformResponse ?? [],
  keepPagesEnvelope,
);

export function getSocialNetworkForRequest(pageType: number) {
  let socialNetwork = PageTypeUtils.getName(pageType);

  if (socialNetwork === PageTypeUtils.name[TYPE.GOOGLE_MY_BUSINESS]) {
    socialNetwork = 'google';
  }

  return socialNetwork;
}

export function getPageTypeFromSocialNetworkRequest(
  socialNetwork: string,
): number {
  let pageType = PageTypeUtils.getNumberFromName(socialNetwork);

  if (socialNetwork === 'google') {
    pageType = TYPE.GOOGLE_MY_BUSINESS;
  }

  return pageType;
}

const SocialLoginApi = {
  requestLoginUrl: async (
    pageType: number,
    params: {
      redirectUrl?: string;
      scopes?: string[];
      tokenType?: SocialLoginTokenType;
    },
  ): Promise<{
    loginUrl: string;
    redirectUrl: string | null;
    userKey?: string;
  }> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return get(`/api/${socialNetwork}/auth/redirect`, {
      baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
      params,
    });
  },
  submitSystemUserToken: async (
    pageType: number,
    data: { accessToken: string },
  ): Promise<{ userKey: string; clientBusinessId: string }> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return post(
      `/api/${socialNetwork}/system-user/token`,
      { accessToken: data.accessToken },
      { baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT },
    );
  },
  addPagesToSocialLogin: async (
    pageType: number,
    data: {
      pages: SocialLoginPage[];
    },
  ): Promise<SocialLoginPage[]> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return post(
      `/api/${socialNetwork}/pages`,
      {
        pages: data.pages,
      },
      {
        baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
      },
    );
  },
  addProfiles: async (
    pageType: number,
    data: {
      timezone: string;
      pages?: SocialLoginPage[];
      // Safe Connect: the selection grouped by the portfolio each page
      // connects through, confirmed in one request.
      portfolios?: SocialLoginConfirmPortfolio[];
      clientBusinessId?: string | null;
      tokenType?: SocialLoginTokenType;
      userKey?: string | null;
      // Safe Connect: how long to wait for social-login, in ms
      // (SAFE_CONNECT_CONFIRM_TIMEOUT unless the host waits less).
      confirmTimeout?: number;
    },
  ): Promise<{ ids: number[]; refused: SocialLoginRefusedPage[] }> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    const socialLoginResponse = await post<{
      data: SocialLoginPage[];
      refused?: SocialLoginRefusedPage[];
    }>(
      `/api/${socialNetwork}/pages`,
      {
        ...(data.portfolios
          ? { portfolios: data.portfolios }
          : { pages: data.pages }),
        ...(data.clientBusinessId
          ? { clientBusinessId: data.clientBusinessId }
          : {}),
        ...(data.tokenType ? { tokenType: data.tokenType } : {}),
        ...(data.userKey ? { userKey: data.userKey } : {}),
      },
      {
        baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
        transformResponse: pagesConfirmTransforms,
        ...(data.portfolios
          ? { timeout: data.confirmTimeout ?? SAFE_CONNECT_CONFIRM_TIMEOUT }
          : {}),
      },
    );

    const ids = await post<number[]>(
      '/pages',
      {
        timezone: data.timezone,
        data: socialLoginResponse.data,
      },
      {
        baseURL: Config.API_SERVICE_ENDPOINT,
      },
    );

    return { ids, refused: socialLoginResponse.refused ?? [] };
  },
  fetchPages: async (
    userKey?: string,
  ): Promise<{
    status: 'waiting' | 'done';
    pages: SocialLoginPage[];
    clientBusinessId?: string | null;
    partial?: boolean;
    businesses?: SocialLoginBusiness[];
    outside?: SocialLoginPage[];
    empty_reason?: SocialLoginEmptyReason | null;
  }> => {
    if (!userKey) {
      throw new Error('User key is required');
    }

    return get('/api/fetch/pages', {
      params: { userKey },
      baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
    });
  },
  connectedPagesExternalIds: async (pageType: number): Promise<string[]> => {
    return get(
      `/settings/authorization/connected-external-page-ids?socialNetwork=${getSocialNetworkForRequest(
        pageType,
      )}`,
      {
        baseURL: Config.API_SERVICE_ENDPOINT,
      },
    );
  },
  toggleShareAccessToken: ({
    isShared,
    pageType,
  }: {
    isShared: boolean;
    pageType: number;
  }) => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return post(
      `/settings/authorization/${socialNetwork}/share-access-token`,
      {
        isShared,
      },
      {
        baseURL: Config.API_SERVICE_ENDPOINT,
      },
    );
  },
};

export default SocialLoginApi;
